import type { FetchResult } from '@apollo/client';
import { ApolloLink } from '@apollo/client';
import { isExecutionPatchIncrementalResult } from '@apollo/client/utilities';
import type { GraphQLErrorExtensions, GraphQLFormattedError } from 'graphql';

// Here's to hoping this grows and maybe even moves to the schema...
enum GraphQLErrorCode {
  'FEATURE_UNAVAILABLE' = 'FEATURE_UNAVAILABLE',
  'EXPIRED_SSO_SESSION' = 'EXPIRED_SSO_SESSION',
  'SESSION_TOKEN_MISSING' = 'SESSION_TOKEN_MISSING',
  'SESSION_INVALID' = 'SESSION_INVALID',
  'INVALID_API_KEY' = 'INVALID_API_KEY',
  'INTERNAL_SERVER_ERROR' = 'INTERNAL_SERVER_ERROR',
}
interface CodedGraphQLError extends GraphQLFormattedError {
  extensions: GraphQLErrorExtensions & { code: GraphQLErrorCode };
}

function isCodedGraphQLError(
  error: GraphQLFormattedError,
): error is CodedGraphQLError {
  return (
    !!error.extensions?.code &&
    Object.values(GraphQLErrorCode).includes(
      error.extensions.code as unknown as GraphQLErrorCode,
    )
  );
}
export interface CatchErrorsContext {
  /**
   * Callback functions which should return true if the error can be
   * caught, otherwise returns false.  Caught errors will not be forwarded to
   * the rest of the link chain.
   */
  catchErrors?: ((error: GraphQLFormattedError) => boolean)[];
}

export const isExpiredSessionError = (error: GraphQLFormattedError) =>
  error.message.toLowerCase().includes('session has expired'.toLowerCase()) ||
  error.message.toLowerCase().includes('session invalid'.toLowerCase()) ||
  (isCodedGraphQLError(error) &&
    error.extensions.code === GraphQLErrorCode.SESSION_INVALID);

export const isMissingSessionError = (error: GraphQLFormattedError) =>
  isCodedGraphQLError(error) &&
  error.extensions.code === GraphQLErrorCode.SESSION_TOKEN_MISSING;

export const isInvalidApiKeyError = (error: GraphQLFormattedError) =>
  isCodedGraphQLError(error) &&
  error.extensions.code === GraphQLErrorCode.INVALID_API_KEY;

export const ignorePermissionsErrors = (error: GraphQLFormattedError) =>
  error.message.includes('NOT_ALLOWED_BY_USER_ROLE') ||
  error.message.includes('Your accounts does not include access to') ||
  error.message.includes('This API key does not have permission to') ||
  error.message.includes('permission denied');

export const ignorePlanErrors = (error: GraphQLFormattedError) => {
  if (isCodedGraphQLError(error)) {
    return error.extensions.code === GraphQLErrorCode.FEATURE_UNAVAILABLE;
  }
  return false;
};

// cloud-router subgraph is permanently offline — suppress its errors globally
// so remaining query sites don't crash pages during Phase 2 cleanup
export const ignoreCloudRouterErrors = (error: GraphQLFormattedError) =>
  error.extensions?.code === 'SUBREQUEST_HTTP_ERROR' &&
  error.extensions?.service === 'cloud-router';

const globalCatchErrorsFns: ((error: GraphQLFormattedError) => boolean)[] = [
  ignoreCloudRouterErrors,
];

export const catchErrors = new ApolloLink((operation, forward) => {
  // attempt to make this somewhat more type-safe
  const { catchErrors: catchErrorsFns }: CatchErrorsContext =
    operation.getContext();

  return forward(operation).map(function handleResult<T extends FetchResult>(
    result: T,
  ): T {
    if (result.errors) {
      const filteredErrors = result.errors.filter((e) => {
        try {
          const wasCaught = [
            ...globalCatchErrorsFns,
            ...(catchErrorsFns ?? []),
          ].some((catchError) => catchError(e));
          return !wasCaught;
        } catch (error) {
          console.warn(error); // eslint-disable-line no-console
        }
        // if there is an error, keep bubbling the error up the chain
        return true;
      });
      return {
        ...result,
        errors: filteredErrors.length > 0 ? filteredErrors : undefined,
      };
    }
    if (isExecutionPatchIncrementalResult(result)) {
      return {
        ...result,
        // recursively handle `@defer`red results that might have errors nested within them
        incremental: result.incremental?.map(handleResult),
      };
    }
    return result;
  });
});
